import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { MapPin, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface GriefMapExerciseProps {
  onBack: () => void;
  userId: string;
}

type ExerciseStep = 'symptoms-before' | 'mapping' | 'symptoms-after' | 'complete';

export const GriefMapExercise = ({ onBack, userId }: GriefMapExerciseProps) => {
  const [step, setStep] = useState<ExerciseStep>('symptoms-before');
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const [startTime] = useState<number>(Date.now());
  const { logExercise, isLogging } = useUniversalTracking();
  
  const [responses, setResponses] = useState({
    lossType: '',
    physicalSensations: '',
    emotionalLayers: '',
    mentalPatterns: '',
    spiritualQuestions: '',
    relationalImpact: '',
    physicalIntensity: [5],
    emotionalIntensity: [5],
    mentalIntensity: [5],
    spiritualIntensity: [5],
    relationalIntensity: [5]
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleComplete = async () => {
    const duration = Math.round((Date.now() - startTime) / 1000 / 60);
    
    try {
      await logExercise({
        exercise_type: 'grief_mapping',
        exercise_name: 'Grief Mapping',
        category: 'grief',
        duration_minutes: duration,
        symptoms_before: symptomsBefore,
        symptoms_after: symptomsAfter,
        completion_data: { 
          lossType: responses.lossType,
          dimensions: {
            physical: responses.physicalIntensity[0],
            emotional: responses.emotionalIntensity[0],
            mental: responses.mentalIntensity[0],
            spiritual: responses.spiritualIntensity[0],
            relational: responses.relationalIntensity[0]
          }
        }
      });
      setStep('complete');
    } catch (error) {
      console.error('Error logging exercise:', error);
      toast.error('Failed to save exercise');
    }
  };

  const handleSaveMap = async (isDraft: boolean = false) => {
    if (!responses.lossType.trim()) {
      toast.error('Please describe what or who you have lost');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('grief_maps')
        .insert({
          user_id: userId,
          loss_type: responses.lossType,
          physical_sensations: responses.physicalSensations || null,
          emotional_layers: responses.emotionalLayers || null,
          mental_patterns: responses.mentalPatterns || null,
          spiritual_questions: responses.spiritualQuestions || null,
          relational_impact: responses.relationalImpact || null,
          physical_intensity: responses.physicalIntensity[0],
          emotional_intensity: responses.emotionalIntensity[0],
          mental_intensity: responses.mentalIntensity[0],
          spiritual_intensity: responses.spiritualIntensity[0],
          relational_intensity: responses.relationalIntensity[0],
          is_draft: isDraft
        });

      if (error) throw error;

      toast.success(isDraft ? 'Draft saved securely' : 'Your grief map has been saved securely', {
        description: 'You can access it from the Grief Tools menu'
      });

      if (!isDraft) {
        setStep('symptoms-after');
      }
    } catch (error) {
      console.error('Error saving grief map:', error);
      toast.error('Failed to save grief map');
    } finally {
      setIsSaving(false);
    }
  };

  // Symptoms Before Step
  if (step === 'symptoms-before') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={onBack} className="mb-4">
          ← Back to Grief Tools
        </Button>

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-light">Interactive Grief Mapping</CardTitle>
                <CardDescription>
                  Map your grief across the six dimensions of QuantumSelf Psychology<sup className="text-[0.6em]">™</sup>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <Card className="bg-gradient-calm border-border/50">
              <CardContent className="py-4 space-y-3">
                <p className="text-base font-medium">Before We Begin</p>
                <p className="text-sm leading-relaxed">
                  Take a moment to check in with how you're feeling right now. This helps track how the exercise affects your wellbeing.
                </p>
              </CardContent>
            </Card>

            <UniversalSymptomTracker
              mode="before"
              onRatingsChange={setSymptomsBefore}
            />
            
            <Button 
              onClick={() => setStep('mapping')} 
              className="w-full"
              disabled={Object.keys(symptomsBefore).length === 0}
            >
              Continue to Grief Mapping
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Symptoms After Step
  if (step === 'symptoms-after') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <CardTitle className="text-xl font-light">How Are You Feeling Now?</CardTitle>
            <CardDescription>
              After completing your grief map, take a moment to check in with yourself.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UniversalSymptomTracker
              mode="after"
              previousRatings={symptomsBefore}
              onRatingsChange={setSymptomsAfter}
            />
            
            <Button 
              onClick={handleComplete} 
              className="w-full"
              disabled={isLogging}
            >
              {isLogging ? 'Saving...' : 'Complete Exercise'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Complete Step
  if (step === 'complete') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardContent className="py-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-medium">Grief Map Complete</h3>
            <p className="text-muted-foreground">
              You've taken an important step in understanding your grief. Your map has been saved and your progress tracked.
            </p>
            <Button onClick={onBack} className="mt-4">
              Return to Grief Tools
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mapping Step (main exercise)
  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Interactive Grief Mapping</CardTitle>
              <CardDescription>
                Map your grief across the six dimensions of QuantumSelf Psychology<sup className="text-[0.6em]">™</sup>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QuantumSelf Introduction */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-base font-medium">Understanding Your Whole Grief Experience</p>
              <p className="text-sm leading-relaxed">
                Grief lives in every dimension of who you are. By mapping it across the physical, emotional, mental, 
                spiritual, energetic, and relational levels of the <span className="font-medium text-foreground">Hierarchy of Healing<sup className="text-[0.6em]">®</sup></span>, 
                you gain clarity about where grief is most intense and what dimension needs the most compassionate attention right now.
              </p>
            </CardContent>
          </Card>

          {/* Loss Type */}
          <div className="space-y-2">
            <label className="text-base font-medium">What or who have you lost?</label>
            <Textarea
              value={responses.lossType}
              onChange={(e) => setResponses({ ...responses, lossType: e.target.value })}
              placeholder="A person, a relationship, health, a job, an identity, a dream, safety..."
              className="min-h-[80px] bg-background/80 text-base"
            />
          </div>

          {/* Physical Dimension */}
          <Card className="border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">🫀</span> Physical Dimension
              </CardTitle>
              <CardDescription className="text-sm">
                How does grief show up in your body?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={responses.physicalSensations}
                  onChange={(e) => setResponses({ ...responses, physicalSensations: e.target.value })}
                  placeholder="Tightness in chest, exhaustion, nausea, tension in shoulders, changes in appetite or sleep..."
                  className="min-h-[100px] bg-background/80 text-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Intensity (1-10)</label>
                <Slider
                  value={responses.physicalIntensity}
                  onValueChange={(value) => setResponses({ ...responses, physicalIntensity: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-sm text-right">{responses.physicalIntensity[0]}/10</p>
              </div>
            </CardContent>
          </Card>

          {/* Emotional Dimension */}
          <Card className="border-soft-accent/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">💧</span> Emotional Dimension
              </CardTitle>
              <CardDescription className="text-sm">
                What emotions are present? (All are welcome - there's no "right" way to grieve)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={responses.emotionalLayers}
                  onChange={(e) => setResponses({ ...responses, emotionalLayers: e.target.value })}
                  placeholder="Sadness, rage, guilt, relief, numbness, longing, fear, love, emptiness..."
                  className="min-h-[100px] bg-background/80 text-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Intensity (1-10)</label>
                <Slider
                  value={responses.emotionalIntensity}
                  onValueChange={(value) => setResponses({ ...responses, emotionalIntensity: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-sm text-right">{responses.emotionalIntensity[0]}/10</p>
              </div>
            </CardContent>
          </Card>

          {/* Mental Dimension */}
          <Card className="border-secondary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">🧠</span> Mental Dimension
              </CardTitle>
              <CardDescription className="text-sm">
                What thoughts, beliefs, or mental patterns are showing up?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={responses.mentalPatterns}
                  onChange={(e) => setResponses({ ...responses, mentalPatterns: e.target.value })}
                  placeholder="'I should have...', rumination, memory loops, difficulty concentrating, questioning reality, replaying moments..."
                  className="min-h-[100px] bg-background/80 text-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Intensity (1-10)</label>
                <Slider
                  value={responses.mentalIntensity}
                  onValueChange={(value) => setResponses({ ...responses, mentalIntensity: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-sm text-right">{responses.mentalIntensity[0]}/10</p>
              </div>
            </CardContent>
          </Card>

          {/* Spiritual Dimension */}
          <Card className="border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">✨</span> Spiritual Dimension
              </CardTitle>
              <CardDescription className="text-sm">
                How has this loss affected your sense of meaning, faith, or connection to something greater?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={responses.spiritualQuestions}
                  onChange={(e) => setResponses({ ...responses, spiritualQuestions: e.target.value })}
                  placeholder="Questioning beliefs, feeling disconnected, seeking signs, wondering 'why?', loss of purpose or meaning..."
                  className="min-h-[100px] bg-background/80 text-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Intensity (1-10)</label>
                <Slider
                  value={responses.spiritualIntensity}
                  onValueChange={(value) => setResponses({ ...responses, spiritualIntensity: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-sm text-right">{responses.spiritualIntensity[0]}/10</p>
              </div>
            </CardContent>
          </Card>

          {/* Relational Dimension */}
          <Card className="border-soft-accent/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">🤝</span> Relational Dimension
              </CardTitle>
              <CardDescription className="text-sm">
                How has this loss affected your relationships with others?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={responses.relationalImpact}
                  onChange={(e) => setResponses({ ...responses, relationalImpact: e.target.value })}
                  placeholder="Feeling isolated, relationships changed, family dynamics shifted, not feeling understood, wanting to withdraw..."
                  className="min-h-[100px] bg-background/80 text-base"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Intensity (1-10)</label>
                <Slider
                  value={responses.relationalIntensity}
                  onValueChange={(value) => setResponses({ ...responses, relationalIntensity: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-sm text-right">{responses.relationalIntensity[0]}/10</p>
              </div>
            </CardContent>
          </Card>

          {/* R3 Reflection */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-4">
              <p className="text-base font-medium">The R3 Method: Recognise, Release, Reprogramme</p>
              
              <div className="space-y-4">
                <div className="text-sm leading-relaxed space-y-2">
                  <p>
                    <span className="font-medium text-foreground">1. Recognise:</span> You've just mapped where grief lives in your system. This awareness itself is powerful-you're bringing conscious attention to what's been held in your body and mind.
                  </p>
                </div>

                <div className="text-sm leading-relaxed space-y-3 p-4 bg-background/50 rounded-lg border border-border/50">
                  <p className="font-medium text-foreground">2. Release: Working with the Grief Energy</p>
                  
                  <p className="text-foreground">Notice which dimension has the highest intensity. This shows you where the grief energy is most concentrated.</p>
                </div>

                <div className="text-sm leading-relaxed space-y-3 p-4 bg-background/50 rounded-lg border border-border/50">
                  <p className="font-medium text-foreground">3. Reprogramme: Integrating Growth from Loss</p>
                  
                  <p className="text-foreground">Grief doesn't need to be "fixed"-but you can consciously choose how to carry it forward.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={() => handleSaveMap(true)}
              disabled={isSaving}
            >
              Save as Draft
            </Button>
            <Button 
              className="flex-1" 
              onClick={() => handleSaveMap(false)}
              disabled={isSaving}
            >
              <Heart className="w-4 h-4 mr-2" />
              Complete Grief Map
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
